import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPanelUser , setPageSize } from "../../slices/panelUserSlice";
import { fetchMessageBox, addMessageBox } from "../../slices/messageBoxSlice";
import PanelUserSearch from "@/components/PanelUserSearch";
import SimplePagination from "@/components/SimplePagination";
import PageSizeSelector from "@/components/PageSizeSelector";

function AddSms({onClose}) {
  const dispatch = useDispatch();

  const {
    panelUserList,
    currentPage,
    totalPages,
    search,
    pageSize
  } = useSelector((state) => state.panelUser);

  const [searchInput, setSearchInput] = useState(search || "");

  const [formData, setFormData] = useState({
    title: "",
    body: "",
    selectedUsers: [],
  });

  // ✅ CURRENT PAGE USERS
  const currentUsers = panelUserList?.result || [];

  // ================= API CALLS =================

  // initial load
  useEffect(() => {
    dispatch(fetchPanelUser({ page: 1, search: "" }));
  }, []);

  // debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchPanelUser({ page: 1, search: searchInput }));
    }, 800);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // page change
  useEffect(() => {
    dispatch(fetchPanelUser({ page: currentPage, search: searchInput }));
  }, [currentPage]);

  // ================= INPUT =================

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ================= USER SELECT =================

  const handleUserChange = (userId) => {
    setFormData((prev) => {
      const isSelected = prev.selectedUsers.includes(userId);

      return {
        ...prev,
        selectedUsers: isSelected
          ? prev.selectedUsers.filter((id) => id !== userId)
          : [...prev.selectedUsers, userId],
      };
    });
  };

  // ================= SELECT ALL (PER PAGE) =================

  const isAllSelected =
    currentUsers.length > 0 &&
    currentUsers.every((u) =>
      formData.selectedUsers.includes(u.pii)
    );

  const isSomeSelected = currentUsers.some((u) =>
    formData.selectedUsers.includes(u.pii)
  );

    const handlePageSizeChange = (size) => {
      dispatch(setPageSize(size)); // update slice
      dispatch(fetchPanelUser({ page: 1, search }));
    }; 
  


  const handleSelectAll = () => {
    const pageIds = currentUsers.map((u) => u.pii);

    if (isAllSelected) {
      // remove current page users
      setFormData((prev) => ({
        ...prev,
        selectedUsers: prev.selectedUsers.filter(
          (id) => !pageIds.includes(id)
        ),
      }));
    } else {
      // add current page users
      setFormData((prev) => ({
        ...prev,
        selectedUsers: [
          ...prev.selectedUsers,
          ...pageIds.filter((id) => !prev.selectedUsers.includes(id)),
        ],
      }));
    }
  };

  // ================= SUBMIT =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    const smsList = formData.selectedUsers.map((pii) => ({
      pii,
      title: formData.title,
      body: formData.body,
    }));

    const resp = await dispatch(addMessageBox({ smsList }));

    if (!resp.error) {
      onClose()
      dispatch(fetchMessageBox({ page: 1}));

      // reset form
      setFormData({
        title: "",
        body: "",
        selectedUsers: [],
      });
    }
  };

  return (
    <div className="p-2 mx-auto md:min-w-[600px] bg-white rounded-lg">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* TITLE */}
        <div>
          <label className="block text-gray-700">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg"
            required
          />
        </div>

        {/* BODY */}
        <div>
          <label className="block text-gray-700">Body</label>
          <textarea
            name="body"
            value={formData.body}
            onChange={handleChange}
            rows="2"
            className="w-full px-3 py-2 border rounded-lg resize-none"
            required
          />
        </div>

        {/* USERS */}
        <div>
          <label className="block text-gray-700 mb-2">Select Users</label>

          {/* SEARCH */}
          <PanelUserSearch
            value={searchInput}
            onChange={setSearchInput}
          />

          {/* LIST */}
          <div className="h-44 overflow-y-auto border p-2 rounded-lg mt-2">

            {/* SELECT ALL */}
            <div className="flex items-center space-x-2 p-1 bg-gray-100 rounded">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(el) => {
                  if (el)
                    el.indeterminate =
                      !isAllSelected && isSomeSelected;
                }}
                onChange={handleSelectAll}
              />
              <label className="font-semibold cursor-pointer">
                Select All (This Page)
              </label>
            </div>

            {/* USERS */}
            {currentUsers.length > 0 ? (
              currentUsers.map((user) => (
                <div
                  key={user.pii}
                  className="flex items-center space-x-2 p-1"
                >
                  <input
                    type="checkbox"
                    checked={formData.selectedUsers.includes(user.pii)}
                    onChange={() => handleUserChange(user.pii)}
                  />
                  <label className="cursor-pointer">
                    {user.email}
                  </label>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">
                No users available
              </p>
            )}
          </div>

          {/* PAGINATION */}
          <div className="mt-3">

             <PageSizeSelector
          pageSize={pageSize}
          setPageSize={handlePageSizeChange}
        />

            <SimplePagination
              currentPage={currentPage}
              setCurrentPage={(page) =>
                dispatch(
                  fetchPanelUser({
                    page,
                    search: searchInput,
                  })
                )
              }
              totalPages={totalPages}
            />
          </div>
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          className="w-full bg-[var(--mainCol)] text-white py-2 rounded-lg"
        >
          Update Messages
        </button>
      </form>
    </div>
  );
}

export default AddSms;